import { catchError, mergeMap, Observable, of } from 'rxjs';
import { Injectable } from '@angular/core';
import { Location } from '@angular/common';

import { UserSession } from '@shared/models';
import {
  CryptoService,
  NavigationService,
  SubprojectService,
  UserRightsService,
} from '.';

@Injectable()
export class RoutingService {
  routesWithSubprojectId: string[] = [
    'dprs',
    'dpr-details',
    'project-setup',
    'report',
  ];
  routesWithDprDetailId: string[] = ['dpr-details'];
  adminRoutes: string[] = ['user-admin/system-admins', 'user-admin/users'];

  constructor(
    private userRightsService: UserRightsService,
    private location: Location,
    private cryptoService: CryptoService,
    private navigationService: NavigationService,
    private subprojectService: SubprojectService
  ) {}

  //#region Getters

  get routePrefix(): string {
    return decodeURIComponent(this.navigationService.routePrefix);
  }

  get subprojectIdFromCurrentRoute(): string {
    const currentRoute = this.location.path().substring(1);
    const splitUrl = currentRoute.split('/');

    if (!currentRoute || splitUrl.length < 1) {
      return null;
    }

    // [0]: clientId; [1]: parent; [2]: subprojectId
    if (!new RegExp(this.routesWithSubprojectId.join('|')).test(splitUrl[1])) {
      return null;
    }

    return decodeURIComponent(splitUrl[2]);
  }

  get dprDetailIdFromCurrentRoute(): string {
    const currentRoute = this.location.path().substring(1);
    const splitUrl = currentRoute.split('/');

    if (!currentRoute || splitUrl.length < 3) {
      return null;
    }

    // [0]: clientId; [1]: parent; [2]: subprojectId; [3]: dprDetailId
    if (!new RegExp(this.routesWithDprDetailId.join('|')).test(splitUrl[1])) {
      return null;
    }

    return decodeURIComponent(splitUrl[3]);
  }

  //#endregion

  getDprDetailsUrl(dprDetails: string): string[] {
    return [
      this.routePrefix,
      'dpr-details',
      this.subprojectIdFromCurrentRoute,
      dprDetails,
    ];
  }

  goToFirstAllowedRoute(
    userDetails: UserSession,
    selectedSubprojectId: number,
    redirectState: string = null,
    hasAccessRights: boolean = true
  ): Observable<boolean> {
    const encryptedId = this.cryptoService.encryptId(selectedSubprojectId);

    if (
      hasAccessRights &&
      redirectState?.includes(userDetails.selectedClientId) &&
      redirectState?.includes(encryptedId)
    ) {
      return of(false);
    }

    const newRedirectState = this.userRightsService.getFirstAllowedState(
      redirectState,
      selectedSubprojectId || null,
      false,
      userDetails
    );

    return newRedirectState
      ? this.navigationService.navigateToRouteWithReplaceAsync(
          ...this.getRedirectRoute(
            newRedirectState,
            userDetails.selectedClientId,
            encryptedId,
            redirectState
          )
        )
      : this.goToFirstAllowedSubproject(userDetails);
  }

  goToFirstAllowedSubproject(userDetails: UserSession): Observable<boolean> {
    const firstAllowedSubproject =
      this.userRightsService.getFirstAllowedSubproject(userDetails);
    if (!firstAllowedSubproject) {
      return this.navigationService.goToNoAccessErrorRouteAsync();
    }

    return this.subprojectService
      .selectSubproject(firstAllowedSubproject.id, userDetails)
      .pipe(
        mergeMap(() => {
          const redirectState =
            this.userRightsService.getFirstAllowedSubprojectState(
              'dprs',
              firstAllowedSubproject.id,
              userDetails
            );
          return redirectState
            ? this.navigationService.navigateToRouteWithReplaceAsync(
                userDetails.selectedClientId,
                redirectState,
                this.cryptoService.encryptId(firstAllowedSubproject.id)
              )
            : this.goToFirstAllowedSubproject(userDetails);
        }),
        catchError(() => {
          return this.goToFirstAllowedRoute(
            userDetails,
            firstAllowedSubproject.id
          );
        })
      );
  }

  navigateToRoute(...routeSegments: string[]): void {
    this.navigationService.navigateToRoute(...routeSegments);
  }

  //#region Private methods

  private getRedirectRoute(
    redirectState: string,
    clientId: string,
    subprojectId: string,
    previousRoute: string
  ): string[] {
    switch (redirectState) {
      case 'dprs':
      // TODO(Improvement) check if we could extract the dpr-details state and pass the new dpr detail id as an extra param ???
      // eslint-disable-next-line no-fallthrough
      case 'dpr-details':
        return [clientId, redirectState, subprojectId];
      case 'project-setup':
        return [
          clientId,
          redirectState,
          subprojectId,
          this.navigationService.getSubRoute(redirectState, previousRoute),
        ];
      case 'project-admin':
        return [
          clientId,
          redirectState,
          this.navigationService.getSubRoute(redirectState, previousRoute),
        ];
      case 'clients':
        return [clientId, redirectState];
      case 'user-admin/system-admins':
      case 'user-admin/users':
        // user-admin/users and user-admin/system-admins needs to be split, because of the reserved character '/'
        const splitRoute = redirectState.split('/');
        return [clientId, splitRoute[0], splitRoute[1]];
    }

    return [];
  }

  //#endregion
}
